import React, { useState } from 'react'
import {
  AppBar,
  Box,
  Chip,
  Dialog,
  IconButton,
  Toolbar,
  Typography
} from '@mui/material'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import CloseIcon from '@mui/icons-material/Close'
import DialpadIcon from '@mui/icons-material/Dialpad'
import ChatTranscript from '../ChatTranscript/ChatTranscript'
import FlowViewer from '../FlowViewer/FlowViewer'

// Mirrors core/models.SessionStatus (mailroom) - W/C/X/I/F are the only values flows_flowsession.status
// ever holds. Colors give the list scannable contrast (a customer stuck mid-session jumps out as
// "Waiting", vs a routine "Completed") without needing to show the session's own uuid, which carries no
// meaning for a business user.
const SESSION_STATUS = {
  W: { label: 'Waiting', color: '#2563eb' },
  C: { label: 'Completed', color: '#16a34a' },
  X: { label: 'Expired', color: '#9ca3af' },
  I: { label: 'Interrupted', color: '#d97706' },
  F: { label: 'Failed', color: '#dc2626' }
}

// A true session's own [created_on, ended_on] window never overlaps another session for the same contact
// (mailroom only ever lets a contact have one waiting session at a time), so filtering the already-loaded
// full message history by that window is an accurate way to show "what a customer sent and received during
// this session" without a second network round trip.
function messagesForSession(messages, session) {
  if (!messages) return []
  const start = new Date(session.created_on).getTime()
  const end = session.ended_on
    ? new Date(session.ended_on).getTime()
    : Date.now()
  return messages.filter(m => {
    const t = new Date(m.created_on).getTime()
    return t >= start && t <= end
  })
}

// A session can visit more than one flow (e.g. USSDStart entering Buy Cover via enter_flow) - this is
// exactly why "session" and "flow run" are different things: a session is the whole dial-in, a flow run is
// just one leg of it.
function flowsSummary(flows) {
  if (!flows || flows.length === 0) return 'Unknown flow'
  return flows.map(f => f.flow).join(' → ')
}

// Renders true sessions (each one an actual customer dial-in, possibly spanning several flows) as a
// clickable list - clicking one expands an inline, scrollable chat transcript scoped to just that
// session's messages, plus the individual flows visited within it. Clicking a flow chip opens that flow's
// real, current definition in a read-only embed of RapidPro's own editor canvas (see FlowViewer) - the
// same view a developer gets in RapidPro itself, not a re-derived summary.
export default function SessionsList({
  sessions,
  sessionsLoading,
  messages,
  messagesLoading
}) {
  const [expandedSession, setExpandedSession] = useState(null)
  const [selectedFlow, setSelectedFlow] = useState(null)

  if (sessionsLoading) {
    return <Typography variant="body2">Loading sessions…</Typography>
  }
  if (!sessions || sessions.length === 0) {
    return <Typography variant="body2">No sessions found.</Typography>
  }

  return (
    <Box>
      <Box
        sx={{
          maxHeight: 'calc(100vh - 300px)',
          overflowY: 'auto',
          border: '1px solid #eee',
          borderRadius: 1
        }}
      >
        {sessions.map(s => {
          const isExpanded = expandedSession === s.uuid
          const status = SESSION_STATUS[s.status] || {
            label: s.status,
            color: '#9ca3af'
          }
          return (
            <Box
              key={s.uuid}
              sx={{
                borderBottom: '1px solid #eee',
                borderLeft: `4px solid ${status.color}`
              }}
            >
              <Box
                onClick={() => setExpandedSession(isExpanded ? null : s.uuid)}
                sx={{
                  padding: 1,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': { backgroundColor: '#fafafa' }
                }}
              >
                <DialpadIcon sx={{ color: '#6B7280' }} />
                <Box>
                  <Typography variant="body1">
                    {flowsSummary(s.flows)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                  >
                    <Box
                      component="span"
                      sx={{ color: status.color, fontWeight: 600 }}
                    >
                      {status.label}
                    </Box>
                    · started {new Date(s.created_on).toLocaleString()}
                    {s.ended_on &&
                      ` · ended ${new Date(s.ended_on).toLocaleString()}`}
                  </Typography>
                </Box>
              </Box>
              {isExpanded && (
                <Box sx={{ paddingBottom: 2, paddingLeft: 1, paddingRight: 1 }}>
                  {s.flows && s.flows.length > 0 && (
                    <Box
                      sx={{
                        marginBottom: 1,
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap'
                      }}
                    >
                      {s.flows.map(f => (
                        <Chip
                          key={f.uuid}
                          icon={<AccountTreeOutlinedIcon />}
                          label={f.flow}
                          size="small"
                          onClick={() => setSelectedFlow(f)}
                          clickable
                        />
                      ))}
                    </Box>
                  )}
                  <ChatTranscript
                    messages={messagesForSession(messages, s)}
                    loading={messagesLoading}
                    maxHeight={350}
                  />
                </Box>
              )}
            </Box>
          )
        })}
      </Box>

      <Dialog
        fullScreen
        open={!!selectedFlow}
        onClose={() => setSelectedFlow(null)}
      >
        <AppBar position="relative" color="default">
          <Toolbar>
            <IconButton
              edge="start"
              onClick={() => setSelectedFlow(null)}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h6" sx={{ marginLeft: 2 }}>
              {selectedFlow ? selectedFlow.flow : ''} (view only)
            </Typography>
          </Toolbar>
        </AppBar>
        {selectedFlow && <FlowViewer flowUuid={selectedFlow.flow_uuid} />}
      </Dialog>
    </Box>
  )
}
